import { AssigneeListItem } from '@cumu/shared';
import { controller, target } from '@github/catalyst';
import RemoteInputElement from '@github/remote-input-element';
import { dispatchChange } from './util';

@controller('assignee-picker')
export class AssigneePickerElement extends HTMLElement {
  @target declare userIdInput: HTMLInputElement;
  @target declare userLoginInput: HTMLInputElement | null;
  @target declare userFirstNameInput: HTMLInputElement | null;
  @target declare userLastNameInput: HTMLInputElement | null;
  @target declare organizationIdInput: HTMLInputElement;
  @target declare organizationLoginInput: HTMLInputElement | null;
  @target declare summary: HTMLElement;
  @target declare remoteInput: RemoteInputElement;
  @target declare suggestions: HTMLElement;
  @target declare contractFormGroupContainer: HTMLElement;
  @target declare assignToMeMenuItem: HTMLTemplateElement | null;
  @target declare assignToMeButton: HTMLButtonElement | null;
  @target declare proximityAnywhereButton: HTMLButtonElement;
  @target declare proximityNearbyButton: HTMLButtonElement;
  @target declare proximityUnavailableMessage: HTMLElement | undefined;

  get value(): AssigneeListItem | null {
    const raw = this.summary
      .querySelector('[data-value]')!
      .getAttribute('data-value')!;
    return JSON.parse(raw) as AssigneeListItem;
  }

  focusMenu() {
    this.remoteInput.input!.focus();
  }

  select() {
    const value = this.value;
    const organization_id = value?.organization_id ?? '';
    const organization_login = value?.organization_login ?? '';
    const user_id = value?.user_id ?? '';
    const user_login = value?.user_login ?? '';
    const user_first_name = value?.user_first_name ?? '';
    const user_last_name = value?.user_last_name ?? '';
    let change = false;
    if (this.organizationIdInput.value !== organization_id) {
      change = true;
      this.organizationIdInput.value = organization_id;
      this.organizationLoginInput?.setAttribute('value', organization_login);
      dispatchChange(this.organizationIdInput);

      if (this.contractFormGroupContainer) {
        const template = organization_id
          ? this.suggestions.querySelector(
              `[data-contracts-for="${organization_id}"]`
            )
          : this.suggestions.querySelector(
              '[data-create-organization-contracts]'
            );
        if (template instanceof HTMLTemplateElement) {
          this.contractFormGroupContainer.innerHTML = '';
          this.contractFormGroupContainer.append(template.content);
        }
      }
    }
    if (this.userIdInput.value !== user_id) {
      change = true;
      this.userIdInput.value = user_id;
      this.userLoginInput?.setAttribute('value', user_login);
      this.userFirstNameInput?.setAttribute('value', user_first_name);
      this.userLastNameInput?.setAttribute('value', user_last_name);
      dispatchChange(this.userIdInput);
      this.assignToMeButton?.parentElement?.toggleAttribute(
        'hidden',
        user_id !== ''
      );
    }
    if (this.remoteInput.input?.value) {
      this.remoteInput.input.value = '';
      dispatchChange(this.remoteInput.input);
    }
    if (change) {
      this.dispatchEvent(
        new CustomEvent('assignee-change', { bubbles: true, detail: value })
      );
    }
  }

  remoteSuccess() {
    // formerly used to insert a clear selection button
  }

  assignToMeAndSave() {
    if (!this.assignToMeMenuItem) {
      throw new Error('assignToMeMenuItem not found');
    }
    this.suggestions.innerHTML = '';
    this.suggestions.append(this.assignToMeMenuItem.content.cloneNode(true));
    this.suggestions.querySelector('button')?.click();
    this.summary.focus();
    this.userIdInput.form?.requestSubmit();
  }

  toggleProximityTab(event: Event) {
    const button = event.currentTarget as HTMLButtonElement;
    const useProximity = button === this.proximityNearbyButton;
    this.proximityAnywhereButton.ariaSelected = String(!useProximity);
    this.proximityNearbyButton.ariaSelected = String(useProximity);
    const proximityUnavailableMessage = this.proximityUnavailableMessage;
    const proximityUnavailable =
      useProximity && Boolean(proximityUnavailableMessage);
    this.remoteInput.toggleAttribute('hidden', proximityUnavailable);
    this.suggestions.toggleAttribute('hidden', proximityUnavailable);
    proximityUnavailableMessage?.toggleAttribute(
      'hidden',
      !proximityUnavailable
    );
    if (proximityUnavailable) {
      button.focus();
      return;
    }
    const url = new URL(this.remoteInput.src, location.origin);
    url.searchParams.set('proximity', String(useProximity));
    this.remoteInput.src = url.toString();
    this.remoteInput.input!.focus();
    dispatchChange(this.remoteInput.input!);
  }
}

declare global {
  interface Window {
    AssigneePickerElement: typeof AssigneePickerElement;
  }
  interface HTMLElementTagNameMap {
    'assignee-picker': AssigneePickerElement;
  }
}
